import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  LmsButton,
  LmsInput,
} from '@shared/ui'
import { ChevronLeft, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useShortVideosState } from '@features/short-videos/state/useShortVideosState'
import { searchPublishedShortVideos } from '@services/firebase/short-videos'
import {
  getShortVideoTranslationText,
  normalizeShortVideoLocale,
} from '@features/short-videos/shared/short-video-helpers'
import { ShortVideosStatusBlock } from '@features/short-videos/shared/ShortVideosStatusBlock'

export function ShortVideoSearchDialog() {
  const { actions, state } = useShortVideosState()
  const { i18n } = useTranslation('translation')
  const locale = normalizeShortVideoLocale(i18n.language)
  const trimmedSearchQuery = state.searchQuery.trim()
  const hasSearchResultsForQuery = state.searchResolvedQuery === trimmedSearchQuery
  const isSearchPreparing = Boolean(trimmedSearchQuery) && (
    state.loading.search || !hasSearchResultsForQuery
  )

  useEffect(() => {
    if (!state.searchDialogOpen) {
      return
    }

    if (!trimmedSearchQuery) {
      actions.setSearchLoading(false)
      actions.setSearchResults([])
      return
    }

    let isMounted = true
    actions.setSearchLoading(true)

    const timeout = window.setTimeout(() => {
      const loadSearchResults = async () => {
        try {
          const results = await searchPublishedShortVideos(trimmedSearchQuery)

          if (!isMounted) {
            return
          }

          actions.setSearchResults(results)
        } finally {
          if (isMounted) {
            actions.setSearchLoading(false)
          }
        }
      }

      void loadSearchResults()
    }, 250)

    return () => {
      isMounted = false
      window.clearTimeout(timeout)
    }
  }, [actions, state.searchDialogOpen, trimmedSearchQuery])

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          actions.setSearchDialogOpen(false)
        }
      }}
      open={state.searchDialogOpen}
    >
      <DialogContent
        className="shortVideosSearchDialog shortVideosSearchDialogContent"
        hideClose
        style={{ translate: 'none' }}
      >
        <DialogTitle className="sr-only">Search short videos</DialogTitle>
        <div className="shortVideosSearchDialogBody">
          <div className="shortVideosSearchToolbar">
            <LmsButton
              aria-label="Close search"
              className="shortVideosSearchBackButton"
              onClick={() => {
                actions.setSearchDialogOpen(false)
              }}
              size="icon"
              variant="ghost"
            >
              <ChevronLeft aria-hidden="true" />
            </LmsButton>
            <LmsInput
              aria-label="Search short videos"
              autoFocus
              onChange={(event) => {
                actions.setSearchQuery(event.target.value)
              }}
              placeholder="Search short videos"
              value={state.searchQuery}
            />
            <span aria-hidden="true" className="shortVideosSearchIcon">
              <Search />
            </span>
          </div>

          {isSearchPreparing ? (
            <ShortVideosStatusBlock
              body="Keep this dialog open while we prepare matching short videos."
              minHeight={260}
              title="Searching short videos"
              isLoading
            />
          ) : (
            <ul className="shortVideosSearchResults">
              {(hasSearchResultsForQuery ? state.searchResults : []).map((video) => {
                const translation = getShortVideoTranslationText(video.translates, locale)

                return (
                  <li key={video.id}>
                    <button
                      className="shortVideosSearchResultButton"
                      onClick={() => {
                        actions.setSelectedVideo(video)
                        actions.setSearchDialogOpen(false)
                      }}
                      type="button"
                    >
                      <span className="shortVideosSearchResultText">
                        <span className="shortVideosSearchResultTitle">
                          {video.title ?? 'Untitled video'}
                        </span>
                        {translation ? (
                          <span className="shortVideosSearchResultDescription">
                            {translation}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {!isSearchPreparing &&
          trimmedSearchQuery &&
          hasSearchResultsForQuery &&
          state.searchResults.length === 0 ? (
            <ShortVideosStatusBlock
              body="This search completed, but no published short videos matched the query."
              minHeight={220}
              title="No short videos matched this search"
              tone="empty"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
